/**
 * AnnotationEditor — SVG annotation layer over an inspection photo.
 * Tools: select/move, arrow, measure line, circle, rectangle, freehand, text.
 * 5 colours, stroke width, undo/redo, delete selected, clear all.
 * Coordinates are normalised (0..1) so annotations scale with the image.
 */
import { useEffect, useRef, useState } from "react";
import {
  MousePointer2, MoveUpRight, Ruler, Circle as CircleIcon, Square, Pencil,
  Type, Undo2, Redo2, Trash2, Eraser,
} from "lucide-react";

export const ANNOT_COLORS = ["#EF4444", "#FACC15", "#3B82F6", "#22C55E", "#FFFFFF"];
const TOOLS = [
  { key: "select", icon: MousePointer2, label: "Select / move" },
  { key: "arrow", icon: MoveUpRight, label: "Arrow" },
  { key: "measure", icon: Ruler, label: "Measurement line" },
  { key: "circle", icon: CircleIcon, label: "Circle" },
  { key: "rect", icon: Square, label: "Rectangle" },
  { key: "free", icon: Pencil, label: "Freehand" },
  { key: "text", icon: Type, label: "Text label" },
];

const newId = () => Math.random().toString(36).slice(2, 10);

// Flatten image + annotations onto a canvas → JPEG data URL (for the PDF).
export async function flattenAnnotations(src, annotations) {
  if (!annotations?.length) return null;
  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = src;
  });
  const W = img.naturalWidth, H = img.naturalHeight;
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0);
  const S = Math.max(W, H);
  for (const a of annotations) {
    ctx.strokeStyle = a.color; ctx.fillStyle = a.color;
    ctx.lineWidth = (a.w || 0.004) * S;
    ctx.lineCap = "round"; ctx.lineJoin = "round";
    const x1 = a.x1 * W, y1 = a.y1 * H, x2 = (a.x2 ?? a.x1) * W, y2 = (a.y2 ?? a.y1) * H;
    if (a.type === "arrow" || a.type === "measure") {
      ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
      const drawHead = (fx, fy, tx, ty) => {
        const ang = Math.atan2(ty - fy, tx - fx);
        const len = Math.max(10, ctx.lineWidth * 3.5);
        ctx.beginPath();
        ctx.moveTo(tx, ty);
        ctx.lineTo(tx - len * Math.cos(ang - 0.42), ty - len * Math.sin(ang - 0.42));
        ctx.moveTo(tx, ty);
        ctx.lineTo(tx - len * Math.cos(ang + 0.42), ty - len * Math.sin(ang + 0.42));
        ctx.stroke();
      };
      drawHead(x1, y1, x2, y2);
      if (a.type === "measure") {
        drawHead(x2, y2, x1, y1);
        if (a.text) {
          const fs = Math.max(16, S * 0.028);
          ctx.font = `bold ${fs}px Arial`;
          const mx = (x1 + x2) / 2, my = (y1 + y2) / 2 - fs * 0.5;
          const tw = ctx.measureText(a.text).width;
          ctx.fillStyle = "rgba(0,0,0,0.55)";
          ctx.fillRect(mx - tw / 2 - 5, my - fs, tw + 10, fs + 8);
          ctx.fillStyle = a.color;
          ctx.fillText(a.text, mx - tw / 2, my);
        }
      }
    } else if (a.type === "circle") {
      ctx.beginPath();
      ctx.ellipse((x1 + x2) / 2, (y1 + y2) / 2, Math.abs(x2 - x1) / 2, Math.abs(y2 - y1) / 2, 0, 0, Math.PI * 2);
      ctx.stroke();
    } else if (a.type === "rect") {
      ctx.strokeRect(Math.min(x1, x2), Math.min(y1, y2), Math.abs(x2 - x1), Math.abs(y2 - y1));
    } else if (a.type === "free") {
      ctx.beginPath();
      (a.points || []).forEach(([px, py], i) => {
        if (i === 0) ctx.moveTo(px * W, py * H); else ctx.lineTo(px * W, py * H);
      });
      ctx.stroke();
    } else if (a.type === "text" && a.text) {
      const fs = Math.max(18, (a.fs || 0.035) * S);
      ctx.font = `bold ${fs}px Arial`;
      const tw = ctx.measureText(a.text).width;
      ctx.fillStyle = "rgba(0,0,0,0.55)";
      ctx.fillRect(x1 - 5, y1 - fs, tw + 10, fs + 8);
      ctx.fillStyle = a.color;
      ctx.fillText(a.text, x1, y1);
    }
  }
  return canvas.toDataURL("image/jpeg", 0.9);
}

export const AnnotationEditor = ({ src, annotations, onChange, colors: allowedColors, testid = "annotation-editor" }) => {
  const palette = (allowedColors && allowedColors.length ? allowedColors : ANNOT_COLORS);
  const [tool, setTool] = useState("arrow");
  const [color, setColor] = useState(palette[0]);
  const [width, setWidth] = useState(0.005);
  const [selectedId, setSelectedId] = useState(null);
  const [drawing, setDrawing] = useState(null);
  const [textInput, setTextInput] = useState(null); // {x, y}
  const [history, setHistory] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const svgRef = useRef(null);
  const dragRef = useRef(null);

  useEffect(() => { setSelectedId(null); setHistory([]); setRedoStack([]); }, [src]);

  const push = (next) => {
    setHistory((h) => [...h.slice(-30), annotations]);
    setRedoStack([]);
    onChange(next);
  };
  const undo = () => {
    if (!history.length) return;
    setRedoStack((r) => [...r, annotations]);
    onChange(history[history.length - 1]);
    setHistory((h) => h.slice(0, -1));
    setSelectedId(null);
  };
  const redo = () => {
    if (!redoStack.length) return;
    setHistory((h) => [...h, annotations]);
    onChange(redoStack[redoStack.length - 1]);
    setRedoStack((r) => r.slice(0, -1));
  };

  const toNorm = (e) => {
    const r = svgRef.current.getBoundingClientRect();
    const cx = (e.touches ? e.touches[0].clientX : e.clientX) - r.left;
    const cy = (e.touches ? e.touches[0].clientY : e.clientY) - r.top;
    return [Math.min(1, Math.max(0, cx / r.width)), Math.min(1, Math.max(0, cy / r.height))];
  };

  const onDown = (e) => {
    if (textInput) return;
    const [x, y] = toNorm(e);
    if (tool === "select") return; // shape handlers manage select/drag
    if (tool === "text") { setTextInput({ x, y, value: "" }); return; }
    const base = { id: newId(), type: tool, color, w: width, x1: x, y1: y, x2: x, y2: y };
    if (tool === "free") base.points = [[x, y]];
    setDrawing(base);
    setSelectedId(null);
  };
  const onMove = (e) => {
    const [x, y] = toNorm(e);
    if (dragRef.current) {
      const { id, mode, sx, sy, orig } = dragRef.current;
      const dx = x - sx, dy = y - sy;
      onChange(annotations.map((a) => {
        if (a.id !== id) return a;
        if (mode === "move") {
          const moved = { ...a, x1: orig.x1 + dx, y1: orig.y1 + dy, x2: (orig.x2 ?? orig.x1) + dx, y2: (orig.y2 ?? orig.y1) + dy };
          if (a.points) moved.points = orig.points.map(([px, py]) => [px + dx, py + dy]);
          return moved;
        }
        if (mode === "end") return { ...a, x2: x, y2: y };
        if (mode === "start") return { ...a, x1: x, y1: y };
        return a;
      }));
      return;
    }
    if (!drawing) return;
    if (drawing.type === "free") setDrawing({ ...drawing, points: [...drawing.points, [x, y]] });
    else setDrawing({ ...drawing, x2: x, y2: y });
  };
  const onUp = () => {
    if (dragRef.current) { dragRef.current = null; return; }
    if (!drawing) return;
    const dist = Math.hypot((drawing.x2 - drawing.x1), (drawing.y2 - drawing.y1));
    if (drawing.type === "free" ? drawing.points.length > 2 : dist > 0.01) {
      const shape = { ...drawing };
      if (shape.type === "measure") {
        const mm = window.prompt("Measurement label (e.g. 1180mm)", "");
        if (mm) shape.text = mm;
      }
      push([...annotations, shape]);
    }
    setDrawing(null);
  };

  const startDrag = (e, a, mode) => {
    if (tool !== "select") return;
    e.stopPropagation();
    const [x, y] = toNorm(e);
    setSelectedId(a.id);
    setHistory((h) => [...h.slice(-30), annotations]);
    setRedoStack([]);
    dragRef.current = { id: a.id, mode, sx: x, sy: y, orig: { ...a, points: a.points ? [...a.points] : undefined } };
  };

  const selected = annotations.find((a) => a.id === selectedId);
  const updateSelected = (patch) => push(annotations.map((a) => (a.id === selectedId ? { ...a, ...patch } : a)));

  const V = 1000; // viewBox scale
  const renderShape = (a, ghost = false) => {
    const p = {
      stroke: a.color, strokeWidth: (a.w || 0.005) * V, fill: "none",
      strokeLinecap: "round", strokeLinejoin: "round",
      opacity: ghost ? 0.8 : 1,
      style: { cursor: tool === "select" ? "move" : "crosshair" },
      onMouseDown: ghost ? undefined : (e) => startDrag(e, a, "move"),
      onTouchStart: ghost ? undefined : (e) => startDrag(e, a, "move"),
    };
    const x1 = a.x1 * V, y1 = a.y1 * V, x2 = (a.x2 ?? a.x1) * V, y2 = (a.y2 ?? a.y1) * V;
    const sel = a.id === selectedId;
    const head = (fx, fy, tx, ty, k) => {
      const ang = Math.atan2(ty - fy, tx - fx);
      const len = Math.max(14, (a.w || 0.005) * V * 3.5);
      return <path key={k} d={`M ${tx} ${ty} L ${tx - len * Math.cos(ang - 0.42)} ${ty - len * Math.sin(ang - 0.42)} M ${tx} ${ty} L ${tx - len * Math.cos(ang + 0.42)} ${ty - len * Math.sin(ang + 0.42)}`} {...p} />;
    };
    const handles = sel && tool === "select" ? (
      <>
        <circle cx={x1} cy={y1} r={14} fill="#fff" stroke="#0EA5E9" strokeWidth={3} style={{ cursor: "grab" }}
          onMouseDown={(e) => startDrag(e, a, "start")} onTouchStart={(e) => startDrag(e, a, "start")} />
        <circle cx={x2} cy={y2} r={14} fill="#fff" stroke="#0EA5E9" strokeWidth={3} style={{ cursor: "grab" }}
          onMouseDown={(e) => startDrag(e, a, "end")} onTouchStart={(e) => startDrag(e, a, "end")} />
      </>
    ) : null;
    if (a.type === "arrow" || a.type === "measure") {
      return (
        <g key={a.id || "ghost"}>
          <line x1={x1} y1={y1} x2={x2} y2={y2} {...p} />
          {head(x1, y1, x2, y2, "h1")}
          {a.type === "measure" && head(x2, y2, x1, y1, "h2")}
          {a.type === "measure" && a.text && (
            <text x={(x1 + x2) / 2} y={(y1 + y2) / 2 - 14} fill={a.color} fontSize={30} fontWeight="bold"
              textAnchor="middle" style={{ paintOrder: "stroke", stroke: "rgba(0,0,0,0.6)", strokeWidth: 6 }}>{a.text}</text>
          )}
          {handles}
        </g>
      );
    }
    if (a.type === "circle") {
      return (
        <g key={a.id || "ghost"}>
          <ellipse cx={(x1 + x2) / 2} cy={(y1 + y2) / 2} rx={Math.abs(x2 - x1) / 2} ry={Math.abs(y2 - y1) / 2} {...p} />
          {handles}
        </g>
      );
    }
    if (a.type === "rect") {
      return (
        <g key={a.id || "ghost"}>
          <rect x={Math.min(x1, x2)} y={Math.min(y1, y2)} width={Math.abs(x2 - x1)} height={Math.abs(y2 - y1)} {...p} />
          {handles}
        </g>
      );
    }
    if (a.type === "free") {
      const d = (a.points || []).map(([px, py], i) => `${i === 0 ? "M" : "L"} ${px * V} ${py * V}`).join(" ");
      return <g key={a.id || "ghost"}><path d={d} {...p} />{sel && tool === "select" && <circle cx={x1} cy={y1} r={14} fill="#fff" stroke="#0EA5E9" strokeWidth={3} />}</g>;
    }
    if (a.type === "text") {
      return (
        <g key={a.id || "ghost"}>
          <text x={x1} y={y1} fill={a.color} fontSize={(a.fs || 0.035) * V} fontWeight="bold"
            style={{ paintOrder: "stroke", stroke: "rgba(0,0,0,0.6)", strokeWidth: 8, cursor: tool === "select" ? "move" : "default" }}
            onMouseDown={(e) => startDrag(e, a, "move")} onTouchStart={(e) => startDrag(e, a, "move")}>{a.text}</text>
          {sel && tool === "select" && <circle cx={x1} cy={y1 + 10} r={14} fill="#fff" stroke="#0EA5E9" strokeWidth={3} />}
        </g>
      );
    }
    return null;
  };

  return (
    <div data-testid={testid}>
      {/* toolbar */}
      <div className="flex flex-wrap items-center gap-1.5 mb-2">
        {TOOLS.map((t) => (
          <button key={t.key} title={t.label} data-testid={`annot-tool-${t.key}`}
            onClick={() => { setTool(t.key); setSelectedId(null); }}
            className={`h-9 w-9 inline-flex items-center justify-center border-2 ${tool === t.key ? "border-[#1E1B4B] bg-[#1E1B4B] text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-400"}`}>
            <t.icon size={15} strokeWidth={2.5} />
          </button>
        ))}
        <span className="w-px h-7 bg-slate-200 mx-1" />
        {palette.map((c) => (
          <button key={c} title={c} data-testid={`annot-color-${c.replace("#", "")}`}
            onClick={() => { setColor(c); if (selected) updateSelected({ color: c }); }}
            className={`h-8 w-8 border-2 ${color === c ? "border-[#1E1B4B] scale-110" : "border-slate-300"}`}
            style={{ backgroundColor: c }} />
        ))}
        <span className="w-px h-7 bg-slate-200 mx-1" />
        <label className="flex items-center gap-1 text-[10px] font-bold text-slate-500 uppercase">
          Size
          <input type="range" min="0.002" max="0.014" step="0.001" value={selected?.w ?? width}
            data-testid="annot-width"
            onChange={(e) => { const v = parseFloat(e.target.value); setWidth(v); if (selected) updateSelected({ w: v }); }}
            className="w-20 accent-[#00F2C3]" />
        </label>
        <span className="w-px h-7 bg-slate-200 mx-1" />
        <button title="Undo" onClick={undo} disabled={!history.length} data-testid="annot-undo"
          className="h-9 w-9 inline-flex items-center justify-center border-2 border-slate-200 bg-white text-slate-600 disabled:opacity-40"><Undo2 size={15}/></button>
        <button title="Redo" onClick={redo} disabled={!redoStack.length} data-testid="annot-redo"
          className="h-9 w-9 inline-flex items-center justify-center border-2 border-slate-200 bg-white text-slate-600 disabled:opacity-40"><Redo2 size={15}/></button>
        <button title="Delete selected" onClick={() => { if (selected) { push(annotations.filter((a) => a.id !== selectedId)); setSelectedId(null); } }}
          disabled={!selected} data-testid="annot-delete"
          className="h-9 w-9 inline-flex items-center justify-center border-2 border-slate-200 bg-white text-red-600 disabled:opacity-40"><Trash2 size={15}/></button>
        <button title="Clear all annotations (original photo is kept)" onClick={() => { if (annotations.length && window.confirm("Remove all annotations from this photo?")) { push([]); setSelectedId(null); } }}
          disabled={!annotations.length} data-testid="annot-clear"
          className="h-9 px-2.5 inline-flex items-center gap-1 border-2 border-slate-200 bg-white text-slate-600 text-[10px] font-bold uppercase disabled:opacity-40"><Eraser size={13}/> Reset</button>
      </div>

      {/* image + svg overlay */}
      <div className="relative select-none touch-none inline-block w-full bg-black/20">
        <img src={src} alt="inspection" className="w-full max-h-[62vh] object-contain pointer-events-none" draggable={false} />
        <svg ref={svgRef} viewBox={`0 0 ${V} ${V}`} preserveAspectRatio="none"
          className="absolute inset-0 w-full h-full"
          style={{ cursor: tool === "select" ? "default" : "crosshair" }}
          onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp}
          onTouchStart={onDown} onTouchMove={onMove} onTouchEnd={onUp}>
          {annotations.map((a) => renderShape(a))}
          {drawing && renderShape(drawing, true)}
        </svg>
        {textInput && (
          <input autoFocus data-testid="annot-text-input"
            className="absolute z-10 px-2 py-1 text-sm font-bold border-2 border-[#00F2C3] bg-black/70 text-white focus:outline-none"
            style={{ left: `${textInput.x * 100}%`, top: `${textInput.y * 100}%` }}
            value={textInput.value}
            onChange={(e) => setTextInput({ ...textInput, value: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === "Enter" && textInput.value.trim()) {
                push([...annotations, { id: newId(), type: "text", color, x1: textInput.x, y1: textInput.y, text: textInput.value.trim(), fs: 0.035 }]);
                setTextInput(null);
              }
              if (e.key === "Escape") setTextInput(null);
            }}
            onBlur={() => {
              if (textInput.value.trim()) push([...annotations, { id: newId(), type: "text", color, x1: textInput.x, y1: textInput.y, text: textInput.value.trim(), fs: 0.035 }]);
              setTextInput(null);
            }}
            placeholder="Type label, Enter to place" />
        )}
      </div>
      <p className="text-[10px] text-slate-500 mt-1">
        Tip: pick a tool and drag on the photo. Use <b>Select</b> to move shapes or drag the round handles to resize / change direction. The original photo is always kept.
      </p>
    </div>
  );
};

export default AnnotationEditor;
